import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { mq, shouldForwardProp, withOpacity } from '@hrworks/design-system';

import { TextStyles } from '../Text';
import { getButtonColorMap } from './Button.colors';
import type { ButtonProps as _ButtonProps } from './Button.types';

const generateInteractiveButtonStyles = (theme: Theme) =>
  css({
    '&&': {
      outline: '2px solid transparent',
    },
    outlineOffset: 2,
    ':focus-visible': {
      outlineColor: theme.sqwTier2Color.border.focus,
      position: 'relative',
    },
  });

type GenerateButtonStylesParams = Pick<
  _ButtonProps,
  'corner' | 'fullWidth' | 'size' | 'color' | 'variant' | 'textAlign'
> & { theme: Theme };

const generateButtonStyles = ({
  theme,
  corner,
  fullWidth,
  size = 'medium',
  color = 'primary',
  variant,
  textAlign,
}: GenerateButtonStylesParams) => {
  const disabled = ':disabled, &[aria-disabled="true"]';
  const notDisabled = ':not(:disabled):not([aria-disabled="true"])';
  const buttonColorMap = getButtonColorMap(theme);

  return css([
    {
      ...(variant !== 'unstyled' && theme.sqwTier2Typography.labelMdSemibold),
      transitionProperty: 'background-color, color, outline-color, opacity',
      transitionDuration: theme.marko.variables.animationDuration.normal,
      appearance: 'none',
      textAlign,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      border: 'none',
      gap: '0.571em',
      padding: '0.571em 0.857em',
      color: theme.sqwTier2Color.text.default,
      borderRadius: corner === 'rounded' ? theme.marko.variables.borderRadius.small : corner === 'pill' ? '5rem' : 0,
      fontSize: theme.marko.typography.sqwFontSizes[size],

      ...(fullWidth && {
        display: 'flex',
        width: '100%',
        justifyContent: textAlign || 'center',
      }),

      ...(variant === 'filled' && {
        backgroundColor: buttonColorMap.filled[color].default,
        color: buttonColorMap.filled[color].color,

        [notDisabled]: {
          [mq.supportsHover]: {
            ':hover': {
              backgroundColor: buttonColorMap.filled[color].hovered,
            },
          },

          ':active': {
            backgroundColor: buttonColorMap.filled[color].pressed,
          },
        },
      }),

      ...(variant === 'ghost' && {
        backgroundColor: buttonColorMap.ghost[color].default,
        color: buttonColorMap.ghost[color].color,
        boxShadow: `inset 0px 0px 0px 2px ${buttonColorMap.ghost[color].color}`,

        [notDisabled]: {
          [mq.supportsHover]: {
            ':hover': {
              backgroundColor: buttonColorMap.ghost[color].hovered,
              ...(color === 'primary' && {
                color: theme.sqwTier2Color.text.brand.hovered,
              }),
            },
          },

          ':active': {
            backgroundColor: buttonColorMap.ghost[color].pressed,
          },
        },
      }),

      ...((variant === 'text' || variant === 'link') && {
        color: color === 'secondary' ? theme.sqwTier2Color.text.default : buttonColorMap.filled[color].default,
        background: 'none',
        padding: 0,

        [mq.supportsHover]: {
          [`${notDisabled}:hover`]: {
            color:
              color === 'secondary'
                ? withOpacity(theme.sqwTier2Color.text.default, '75%')
                : buttonColorMap.filled[color].hovered,
          },
        },
      }),

      ...(variant === 'subtle' && {
        backgroundColor: buttonColorMap.subtle[color].default,
        color: buttonColorMap.subtle[color].color,

        [`&:hover${notDisabled}`]: {
          [mq.supportsHover]: {
            backgroundColor: buttonColorMap.subtle[color].hovered,
            ...(color === 'primary' && {
              color: theme.sqwTier2Color.text.brand.hovered,
            }),
          },

          ':active': {
            backgroundColor: buttonColorMap.subtle[color].pressed,
            ...(color === 'primary' && {
              color: theme.sqwTier2Color.text.brand.pressed,
            }),
          },
        },
      }),

      [disabled]: {
        opacity: theme.marko.variables.opacity.disabled,
        cursor: 'not-allowed',
      },
    },
    generateInteractiveButtonStyles(theme),
    variant === 'link' && TextStyles.interactiveTextStyles(theme),
  ]);
};

type ButtonProps = {
  $corner: _ButtonProps['corner'];
  $fullWidth: _ButtonProps['fullWidth'];
  $size: Required<_ButtonProps>['size'];
  $color: Required<_ButtonProps>['color'];
  $variant: _ButtonProps['variant'];
  $textAlign: _ButtonProps['textAlign'];
};

const Button = styled('button', {
  shouldForwardProp: (prop: string) => prop !== 'as' && shouldForwardProp(prop),
})<ButtonProps>(({ theme, $corner, $fullWidth, $size, $color, $variant, $textAlign }) =>
  generateButtonStyles({
    theme,
    corner: $corner,
    fullWidth: $fullWidth,
    size: $size,
    color: $color,
    variant: $variant,
    textAlign: $textAlign,
  }),
);

const Icon = styled.span({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  width: '1rem',
  height: '1rem',
  pointerEvents: 'none',
});

export const S = {
  generateInteractiveButtonStyles,
  generateButtonStyles,
  Button,
  Icon,
} as const;
